import { Injectable } from '@angular/core';
import { ListApiService } from './api';
import { ListItemDto } from '../dto';
import { Observable, of, Subject } from 'rxjs';
import { delay, finalize, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ListsCacheService {

  private cache: Record<string, ListItemDto> = {};

  private creationDate: number = Date.now();

  private timeToLive: number = 2 * 60 * 60 * 1000;
  private requestDelay: number = 200;
  private keySeparator: string = ',';

  private requestQueue: Record<string, Subject<ListItemDto[]>> = {};
  private inProgressQueue: Record<string, boolean> = {};

  constructor(private listApiService: ListApiService) {
  }

  getCacheItems(idList: number[]): Observable<ListItemDto[]> {
    this.cacheAutoClean();

    if (!idList || !idList.length){
      return of([]);
    }

    if (idList.every(id => !!this.cache[String(id)])) {
      return of(idList.map(id => this.cache[id]));
    }

    const idKey: string = idList.sort().join(this.keySeparator);

    if (this.inProgressQueue[idKey]) {
      return this.requestQueue[idKey];
    }

    const requestSubject = new Subject<ListItemDto[]>();
    this.requestQueue[idKey] = requestSubject;
    this.runNextRequest();
    return requestSubject.asObservable();
  }

  setCacheItem(listItem: ListItemDto): void {
    if (listItem) {
      this.cache[String(listItem.id)] = listItem;
    }
  }

  setCacheItems(lists: ListItemDto[]): void {
    if (lists) {
      this.cache = {
        ...this.cache,
        ...lists.reduce((acc, item) => ({
          ...acc,
          [String(item.id)]: item,
        }), {}),
      };
    }
  }

  cleanCache(): void {
    this.cache = {};
    this.creationDate = Date.now();
  }

  private cacheAutoClean(): void {
    if (Date.now() - this.creationDate > this.timeToLive) {
      this.cleanCache();
    }
  }

  private runNextRequest(): void {
    for (const key of Object.keys(this.requestQueue)){
      if (this.inProgressQueue[key]) {
        continue;
      }
      this.inProgressQueue[key] = true;
      this.getItemsInternal(key.split(this.keySeparator)).pipe(
        delay(this.requestDelay),
        finalize(() => {
          delete this.requestQueue[key];
          delete this.inProgressQueue[key];
        })
      ).subscribe({
        next: (lists: ListItemDto[]) => this.requestQueue[key].next(lists),
        error: (error) => this.requestQueue[key].error(error),
        complete: () => this.requestQueue[key].complete(),
      })
    }
  }

  private getItemsInternal(idList: string[]): Observable<ListItemDto[]> {
   return this.listApiService.getListsByIds(idList).pipe(
      tap((lists: ListItemDto[]) => this.setCacheItems(lists)),
    );
  }
}
